import React, { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Loader, EmptyState, Pagination } from '../../components/Loader'
import { ordersApi } from '../../services/api'
import { Order, ORDER_STATUS_LABELS, ORDER_TYPE_LABELS } from '../../types'
import { formatToman, formatDateTimeFa, toFaDigits } from '../../utils/format'

const STATUS_BADGE: Record<Order['status'], string> = {
  PENDING: 'badge-warning',
  CONFIRMED: 'badge-gold',
  PROCESSING: 'badge bg-blue-100 text-blue-800',
  COMPLETED: 'badge-success',
  CANCELLED: 'badge-danger'
}

/** مجموع وزن سفارش به گرم */
const orderGrams = (order: Order) =>
  (order.items || []).reduce((sum, i) => sum + (i.product?.weight || 0) * i.quantity, 0)

const Orders: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([])
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [loading, setLoading] = useState(true)
  const [typeFilter, setTypeFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await ordersApi.getMyOrders({
        type: typeFilter || undefined,
        status: statusFilter || undefined,
        page,
        limit: 10
      })
      if (res.data.success && res.data.data) {
        setOrders(res.data.data.orders)
        setTotalPages(res.data.data.pagination.totalPages)
      }
    } catch {
      setOrders([])
    } finally {
      setLoading(false)
    }
  }, [page, typeFilter, statusFilter])

  useEffect(() => {
    load()
  }, [load])

  return (
    <div className="container-main py-10">
      <h1 className="text-2xl font-black text-gray-900 mb-1">سفارش‌های من</h1>
      <p className="text-sm text-gray-500 mb-6">سوابق خرید و فروش شما</p>

      <div className="flex items-center gap-3 mb-6">
        <select className="input !w-auto" value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(1) }}>
          <option value="">همه نوع‌ها</option>
          <option value="BUY">خرید</option>
          <option value="SELL">فروش</option>
        </select>
        <select className="input !w-auto" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1) }}>
          <option value="">همه وضعیت‌ها</option>
          {Object.entries(ORDER_STATUS_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader /></div>
      ) : orders.length === 0 ? (
        <EmptyState title="سفارشی یافت نشد" description="اولین خرید یا فروش خود را انجام دهید." />
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-2">
            {orders.map((order) => (
              <Link key={order.id} to={`/orders/${order.id}`} className="card p-5 block hover:shadow-md">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-black text-gray-900" dir="ltr">{order.orderNumber}</span>
                  <span className={`badge ${STATUS_BADGE[order.status]}`}>{ORDER_STATUS_LABELS[order.status]}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <span className={`badge ${order.type === 'BUY' ? 'badge-success' : 'badge-gold'}`}>
                      {ORDER_TYPE_LABELS[order.type]}
                    </span>
                    <span className="text-gray-500 text-xs">{toFaDigits(order.items.length)} قلم</span>
                  </div>
                  <div className="font-black text-primary-700">{formatToman(order.total)}</div>
                </div>
                <div className="text-xs text-gray-500 mt-2 space-y-0.5">
                  <div>
                    وزن کل:{' '}
                    <b className="text-gray-700">{toFaDigits(orderGrams(order))} گرم</b>
                  </div>
                  {(order.items || []).map((i) => (
                    <div key={i.id} className="text-[11px] text-gray-400">
                      {i.product?.name} × {toFaDigits(i.quantity)}
                      {i.product?.weight ? ` (${toFaDigits(i.product.weight * i.quantity)} گرم)` : ''}
                    </div>
                  ))}
                </div>
                <div className="text-xs text-gray-400 mt-2">{formatDateTimeFa(order.createdAt)}</div>
              </Link>
            ))}
          </div>
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </>
      )}
    </div>
  )
}

export default Orders